import React from 'react';
import Linkify from 'react-linkify';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import classNames from 'classnames';

import { Paper, Translation, Link } from '../../common';
import downloadIcon from '../../../assets/img/notification/icon-download.svg';

import { iban, bic } from '../../../utils/regex';
import { success } from '../../../utils/toastr';
import { onClickWrapper } from '../../../utils/ui';
import { connectTo, shrinkName } from '../../../utils';
import { getOneTimeToken } from '../../../utils/token';
import {
  getUrlParams,
  getSearchUrl,
  getPureUrl,
  getSearchString,
} from '../../../utils/httpUtil';

import { CUSTOMER_MESSAGE_TYPES_MAP } from '../../../constants/post-box';
import { matchMaxWidth } from '../../../utils/styles';

import screens from '../../../assets/styles/mixins/_screens.scss';

export const maxTextSize = 150;

const MAX_DOC_SIZE_LG = 50;
const MAX_DOC_SIZE_TABLET = 28;
const MAX_DOC_SIZE_MOBILE = 20;

const INTERNAL_NOTE_KEY = 'UNIC_COMMUNICATION_INTERNALNOTE';
const CLIENT_EMAIL_KEY = 'UNIC_COMMUNICATION_CLIENT';

export const createLink = (url, authToken) => {
  let regexp = new RegExp(/\?/);
  return regexp.test(url)
    ? url + '&jwtToken=' + authToken
    : url + '?jwtToken=' + authToken;
};

export const convertUniCodes = (text) => {
  if (text && text.indexOf('&#') >= 0) {
    return text.replace(/&#(\w+);/g, (m, code) => String.fromCodePoint(code));
  }
  return text;
};

export const formatText = (text, isOpen, maxSize) => {
  const convertedText = convertUniCodes(text);
  if (convertedText.length > maxSize) {
    return isOpen
      ? convertedText
      : convertedText.slice(0, maxTextSize) + ' ...';
  } else {
    return convertedText;
  }
};

export const AddCopyIbanBic = ({ text, className, onCopy }) => {
  const copyRegex = new RegExp(iban.source + '[,\\n] BIC:? ' + bic.source, 'g');

  let match;
  const matches = [];
  while ((match = copyRegex.exec(text))) {
    matches.push({ index: match.index, value: match[0] });
  }

  if (matches.length) {
    let lastIndex = 0;
    const elements = [];
    matches.forEach(({ index, value }) => {
      if (index > lastIndex) {
        elements.push(
          <Linkify
            properties={{
              onClick: onClickWrapper(
                'page.postbox.component.notification.link'
              ),
            }}
          >
            {text.substring(lastIndex, index)}
          </Linkify>
        );
      }

      elements.push(
        <CopyToClipboard
          onCopy={onClickWrapper(
            'page.postbox.component.notification.copy-to-clipboard',
            onCopy
          )}
          text={value}
        >
          <span className={className}>{value}</span>
        </CopyToClipboard>
      );
      lastIndex = index + value.length;
    });

    if (text.length > lastIndex) {
      elements.push(
        <Linkify
          properties={{
            onClick: onClickWrapper('page.postbox.component.notification.link'),
          }}
        >
          {text.substring(lastIndex)}
        </Linkify>
      );
    }

    return elements.length === 1
      ? elements[0]
      : elements.map((el, idx) => ({
          ...el,
          key: idx,
        }));
  } else {
    return (
      <Linkify
        properties={{
          onClick: onClickWrapper('page.postbox.component.notification.link'),
        }}
      >
        {text}
      </Linkify>
    );
  }
};

class Notification extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isOpen: false,
      matchesMobile: matchMaxWidth(screens.mobileWidth).matches,
      matchesTablet: matchMaxWidth(screens.tabletWidth).matches,
    };
  }

  handleMatchChange = (fieldName) => (e) => {
    this.setState({ [fieldName]: e.matches });
  };

  componentDidMount() {
    matchMaxWidth(screens.mobileWidth).addEventListener(
      'change',
      this.handleMatchChange('matchesMobile')
    );
    matchMaxWidth(screens.tabletWidth).addEventListener(
      'change',
      this.handleMatchChange('matchesTablet')
    );
  }

  handleClick = () => {
    this.setState({ isOpen: !this.state.isOpen });
  };

  onTextCopy = () => success('Copied!');

  onAttachmentClick = (url) => () => {
    getOneTimeToken().then((token) => {
      if (token) {
        const pureUrl = getPureUrl(url);
        const query = getUrlParams(getSearchString(url));

        window.open(
          getSearchUrl({
            url: pureUrl,
            query: {
              ...query,
              oneTimeToken: token,
            },
          }),
          '_blank'
        );
      }
    });
  };

  render() {
    const {
      styles,
      text,
      datetime,
      type,
      mainDoc,
      docs,
      subject,
      name,
      translations,
    } = this.props;
    const { matchesMobile, matchesTablet, isOpen } = this.state;
    const isInternalNote = type === CUSTOMER_MESSAGE_TYPES_MAP.INTERNAL;
    const isClient = type === CUSTOMER_MESSAGE_TYPES_MAP.CLIENT;

    const isInternal = isInternalNote || isClient;
    const headerKey = isInternalNote
      ? INTERNAL_NOTE_KEY
      : isClient
      ? CLIENT_EMAIL_KEY
      : '';

    const header =
      (isInternal
        ? (translations[headerKey] ? translations[headerKey] + ' - ' : '') +
          (name ? name + ': ' : '')
        : '') +
      (datetime + (subject && !isInternal ? ' | ' + subject : ''));

    return (
      <div className={styles[`notification__wrapper_${type}`]}>
        <Paper className={styles[`notification__container_${type}`]}>
          <div
            className={classNames(
              styles['notification__content'],
              styles[`notification__content_${type}`]
            )}
          >
            <div>
              {mainDoc ? (
                <div
                  onClick={
                    isInternal ? undefined : this.onAttachmentClick(mainDoc.url)
                  }
                  className={classNames(
                    isInternal ? '' : styles['notification__date-link'],
                    styles[`notification__date_${type}`]
                  )}
                >
                  {header}
                </div>
              ) : (
                <span
                  className={classNames(
                    isInternal ? '' : styles['notification__date'],
                    styles[`notification__date_${type}`]
                  )}
                >
                  {header}
                </span>
              )}
            </div>
            {text ? (
              <>
                <div className={styles[`notification__body_${type}`]}>
                  <AddCopyIbanBic
                    text={formatText(text, isOpen, maxTextSize)}
                    onCopy={this.onTextCopy}
                    className={styles['copy']}
                  />
                </div>

                {convertUniCodes(text).length <= maxTextSize ? null : (
                  <Translation
                    id={`page.postbox.component.notification.show-${
                      isOpen ? 'less' : 'more'
                    }`}
                    onClick={this.handleClick}
                    className={styles['notification__show-more']}
                  >
                    {isOpen ? 'SHOW_LESS' : 'SHOW_MORE'}
                  </Translation>
                )}
                {docs ? (
                  <div className={styles['notification__attachments']}>
                    {docs.map(({ url, name, icon }) => (
                      <div
                        className={styles['notification__attachment-wrapper']}
                      >
                        <div
                          className={styles['notification__attachment']}
                          onClick={this.onAttachmentClick(url)}
                        >
                          <img
                            className={styles['notification__attachment-img']}
                            src={icon}
                            alt="attachment-icon"
                          />
                          <div
                            className={styles['notification__attachment-name']}
                          >
                            {shrinkName(
                              name,
                              !matchesMobile && !matchesTablet
                                ? MAX_DOC_SIZE_LG
                                : matchesMobile
                                ? MAX_DOC_SIZE_MOBILE
                                : MAX_DOC_SIZE_TABLET
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
          {mainDoc ? (
            <img
              className={classNames(
                styles['download-ref__icon'],
                styles['notification__main-attachment']
              )}
              src={mainDoc.icon ? mainDoc.icon : downloadIcon}
              alt="download link"
              onClick={this.onAttachmentClick(mainDoc.url)}
            />
          ) : null}
        </Paper>
      </div>
    );
  }
}

export default connectTo(
  ({ translations: { translations } }) => ({ translations }),
  {},
  Notification
);
